import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useQuery from 'hooks/apollo/useQuery';
import { useTranslation } from 'react-i18next';
import useSubscription from 'hooks/apollo/useSubscription';
import { useToast } from 'hooks/toasts/useToast';
import { usePermission } from 'providers/RightsHooks';
import { removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { CONTENT_TYPE_X_METADATA, METADATA_LIST_QUERY } from '../../queries';
import { CONTENT_TYPE_X_METADATA_DELETED } from '../../subscriptions';
import { Typography, Button, FakeText, Card } from '@totalsoft/rocket-ui';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Box, TableBody, TableRow, TableCell } from '@mui/material';
import { StyledTable, StyledGrayIcon } from 'components/common/Styles';
import { ADMIN_DMS_WRITE } from 'constants/rightsCodes';
import MetadataItemComponent from './MetadataItemComponent';
import FlexBox from 'components/common/FlexBox';
import { metadataModalOpenVar } from '../../variables';
import MetadataModalComponent from './MetadataModalComponent';

const getEmptyContentXMetadata = contentTypeId => ({
  id: null,
  contentTypeId,
  mandatory: false
});

const ContentTypeXMetadataContainer = ({ contentTypeId }) => {
  const [inAdd, setInAdd] = useState(false);

  const { t } = useTranslation();
  const addToast = useToast();
  const canWrite = usePermission(ADMIN_DMS_WRITE);

  const { data: metadata } = useQuery(METADATA_LIST_QUERY);

  const { data, loading } = useQuery(CONTENT_TYPE_X_METADATA, {
    skip: !contentTypeId,
    variables: { contentTypeId }
  });

  const contentTypeXMetadataOptions = useMemo(() => {
    if (!metadata || !metadata.metadata) {
      return [];
    }

    return metadata.metadata.map(metadata => ({
      id: metadata.id,
      name: `${metadata.code} / ${metadata.name}`
    }));
  }, [metadata]);

  const contentTypeXMetadata = useMemo(() => {
    const result = data?.contentTypeXMetadata || [];
    if (inAdd) return [getEmptyContentXMetadata(contentTypeId), ...result];
    else return result;
  }, [data, inAdd, contentTypeId]);

  useSubscription(CONTENT_TYPE_X_METADATA_DELETED, {
    onData: ({
      data: {
        data: {
          contentTypeXMetadataDeleted: { id }
        }
      },
      client
    }) => {
      removeCachedDetail(client, { name: 'contentTypeXMetadata', value: CONTENT_TYPE_X_METADATA }, { contentTypeId }, x => x.id === id, [
        'id'
      ]);
      addToast(t('Metadata.MetadataDeleted'), 'success');
    }
  });

  const handleEditMetadata = useCallback(() => {
    metadataModalOpenVar({ open: true });
  }, []);

  return (
    <>
      <Card
        icon={DescriptionIcon}
        iconColor='primary'
        title={t('Metadata.Title')}
        actions={
          <FlexBox gap={0.5}>
            <Button startIcon={<EditIcon />} onClick={handleEditMetadata} sx={{ whiteSpace: 'nowrap' }}>
              {t('Metadata.Title')}
            </Button>
            <Button startIcon={<AddIcon />} onClick={() => setInAdd(true)} disabled={!canWrite || inAdd} sx={{ whiteSpace: 'nowrap' }}>
              {t('Add')}
            </Button>
          </FlexBox>
        }
      >
        <StyledTable>
          <TableBody>
            <TableRow>
              {loading && (
                <TableCell colSpan={2}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!loading && contentTypeXMetadata.length === 0 && (
                <TableCell colSpan={4}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon lsGrayIcon fontSize='small'>
                      error
                    </StyledGrayIcon>
                    <Typography color='textSecondary'>{t('ResultsNotFound')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!loading &&
              contentTypeXMetadata.map((metadata, index) => {
                return (
                  <MetadataItemComponent
                    key={metadata.id}
                    index={index}
                    metadata={metadata}
                    contentTypeXMetadataList={contentTypeXMetadata}
                    contentTypeXMetadataOptions={contentTypeXMetadataOptions}
                    canWrite={canWrite}
                    onCancelAdd={() => setInAdd(false)}
                    contentTypeId={contentTypeId}
                  />
                );
              })}
          </TableBody>
        </StyledTable>
      </Card>
      <MetadataModalComponent />
    </>
  );
};

ContentTypeXMetadataContainer.propTypes = {
  contentTypeId: PropTypes.number
};

export default ContentTypeXMetadataContainer;
